import { useEffect, useState } from "react";
import { getAdminProfile } from "../../features/adminAuth/adminAuthStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const UsersPage = () => {
  const [activeTab, setActiveTab] = useState("users");
  const signedInAdmin = getAdminProfile();
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [emailFilter, setEmailFilter] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [actionLoadingUserId, setActionLoadingUserId] = useState("");
  const [editingWalletUserId, setEditingWalletUserId] = useState("");
  const [walletEditValue, setWalletEditValue] = useState("");

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/auth/users`);
      const payload = await response.json();

      if (!response.ok) {
        setUsersError(payload.message || "Failed to fetch users.");
        return;
      }

      setRegisteredUsers(Array.isArray(payload.data) ? payload.data : []);
    } catch (error) {
      setUsersError("Unable to connect to backend service.");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteUser = async (userId, email) => {
    const approved = window.confirm(`Delete user ${email}?`);
    if (!approved) {
      return;
    }

    setActionLoadingUserId(userId);
    setUsersError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/auth/users/${userId}`, {
        method: "DELETE",
      });
      const payload = await response.json();

      if (!response.ok) {
        setUsersError(payload.message || "Failed to delete user.");
        return;
      }

      await loadUsers();
    } catch (error) {
      setUsersError("Unable to connect to backend service.");
    } finally {
      setActionLoadingUserId("");
    }
  };

  const toggleUserSuspension = async (user) => {
    const endpoint = user.status === "suspended" ? "unsuspend" : "suspend";
    setActionLoadingUserId(user._id);
    setUsersError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/auth/users/${user._id}/${endpoint}`, {
        method: "PATCH",
      });
      const payload = await response.json();

      if (!response.ok) {
        setUsersError(payload.message || "Failed to update user status.");
        return;
      }

      await loadUsers();
    } catch (error) {
      setUsersError("Unable to connect to backend service.");
    } finally {
      setActionLoadingUserId("");
    }
  };

  const startWalletEdit = (user) => {
    setEditingWalletUserId(user._id);
    setWalletEditValue(String(Number(user.walletBalance || 0).toFixed(2)));
    setUsersError("");
  };

  const cancelWalletEdit = () => {
    setEditingWalletUserId("");
    setWalletEditValue("");
  };

  const saveWalletEdit = async (userId) => {
    const parsedBalance = Number(walletEditValue);
    if (Number.isNaN(parsedBalance) || parsedBalance < 0) {
      setUsersError("Wallet balance must be a valid number greater than or equal to 0.");
      return;
    }

    setActionLoadingUserId(userId);
    setUsersError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/auth/users/${userId}/wallet`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ walletBalance: parsedBalance }),
      });
      const payload = await response.json();

      if (!response.ok) {
        setUsersError(payload.message || "Failed to update wallet balance.");
        return;
      }

      cancelWalletEdit();
      await loadUsers();
    } catch (error) {
      setUsersError("Unable to connect to backend service.");
    } finally {
      setActionLoadingUserId("");
    }
  };

  return (
    <section className="admin-page-card">
      <h2>Users</h2>
      <p>Manage admin and customer accounts from this module.</p>

      <div className="users-tabs" role="tablist" aria-label="Users sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "users"}
          className={activeTab === "users" ? "users-tab users-tab-active" : "users-tab"}
          onClick={() => setActiveTab("users")}
        >
          Users
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "admins"}
          className={activeTab === "admins" ? "users-tab users-tab-active" : "users-tab"}
          onClick={() => setActiveTab("admins")}
        >
          Admin
        </button>
      </div>

      {activeTab === "users" ? (
        <section className="users-panel" role="tabpanel" aria-label="All users">
          <div className="users-panel-header">
            <h3>All Users</h3>
          </div>

          {/* Email filter input */}
          <div style={{ margin: "12px 0" }}>
            <input
              type="text"
              placeholder="Filter by email..."
              value={emailFilter}
              onChange={e => setEmailFilter(e.target.value)}
              style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #cce3db", width: 260 }}
            />
          </div>

          {!usersLoading && registeredUsers.length > 0 && (
            <div className="users-wallet-summary">
              <span className="users-wallet-summary-label">Total wallet balance across all users</span>
              <span className="users-wallet-summary-value">
                ${registeredUsers
                  .reduce((sum, u) => sum + Number(u.walletBalance || 0), 0)
                  .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="users-wallet-summary-count">{registeredUsers.length} account{registeredUsers.length !== 1 ? "s" : ""}</span>
            </div>
          )}

          {usersError ? <p className="auth-error">{usersError}</p> : null}

          <div className="users-table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Wallet</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr>
                    <td colSpan="6" className="users-empty-row">
                      Loading users...
                    </td>
                  </tr>
                ) : registeredUsers.filter(user =>
                    user.email.toLowerCase().includes(emailFilter.trim().toLowerCase())
                  ).length === 0 ? (
                  <tr>
                    <td colSpan="6" className="users-empty-row">
                      No users found yet.
                    </td>
                  </tr>
                ) : (
                  registeredUsers
                    .filter(user =>
                      user.email.toLowerCase().includes(emailFilter.trim().toLowerCase())
                    )
                    .map((user) => (
                      <tr key={user._id || user.email}>
                        <td>{user.email.split("@")[0]}</td>
                        <td>{user.email}</td>
                        <td>
                          {editingWalletUserId === user._id ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={walletEditValue}
                              onChange={(event) => setWalletEditValue(event.target.value)}
                              className="wallet-edit-input"
                            />
                          ) : (
                            <span className="wallet-badge">${Number(user.walletBalance || 0).toFixed(2)}</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={
                              user.status === "suspended"
                                ? "status-badge status-suspended"
                                : "status-badge status-active"
                            }
                          >
                            {user.status || "active"}
                          </span>
                        </td>
                        <td>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>
                          <div className="provider-row-actions">
                            {editingWalletUserId === user._id ? (
                              <>
                                <button
                                  type="button"
                                  className="row-btn row-btn-primary"
                                  onClick={() => saveWalletEdit(user._id)}
                                  disabled={actionLoadingUserId === user._id}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="row-btn row-btn-muted"
                                  onClick={cancelWalletEdit}
                                  disabled={actionLoadingUserId === user._id}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="row-btn row-btn-muted"
                                  onClick={() => startWalletEdit(user)}
                                  disabled={actionLoadingUserId === user._id}
                                >
                                  Edit Wallet
                                </button>
                                <button
                                  type="button"
                                  className="row-btn row-btn-warning"
                                  onClick={() => toggleUserSuspension(user)}
                                  disabled={actionLoadingUserId === user._id}
                                >
                                  {user.status === "suspended" ? "Unsuspend" : "Suspend"}
                                </button>
                                <button
                                  type="button"
                                  className="row-btn row-btn-danger"
                                  onClick={() => deleteUser(user._id, user.email)}
                                  disabled={actionLoadingUserId === user._id}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="users-panel" role="tabpanel" aria-label="Admins">
          <div className="users-panel-header">
            <h3>Admins</h3>
          </div>

          <div className="users-table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {signedInAdmin ? (
                  <tr>
                    <td>{signedInAdmin.name || "Admin"}</td>
                    <td>{signedInAdmin.email}</td>
                    <td>{signedInAdmin.role || "admin"}</td>
                    <td>Signed in</td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan="4" className="users-empty-row">
                      No admins found yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </section>
  );
};

export default UsersPage;
